import React from "react";
import { FolderType } from "@/types/productTypes";
import { useDrop } from "react-dnd";
import { useAdminStore } from "../../../../store/useAdminStore";
import { ConnectDropTarget } from "react-dnd";
import { Button } from "@/Components/ui/button";
import { FolderIcon, Calendar, MoreHorizontal } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface FolderCardProps {
   folder: FolderType;
   onNavigate?: (id: number) => void;
   onDelete?: (id: number) => void;
   onEdit?: (folder: FolderType) => void;
   view?: "grid" | "list";
}

const FolderCard: React.FC<FolderCardProps> = ({
   folder,
   onNavigate,
   onDelete,
   onEdit,
   view = "grid",
}) => {
   const { assignImageToFolder, allImages, setOptimisticImages } =
      useAdminStore();

   const [{ isOver, canDrop }, drop] = useDrop(
      () => ({
         accept: "IMAGE",
         drop: async (item: { id: number }) => {
            // Optimistically update the UI
            const optimisticImages = allImages.map((img) =>
               img.id === item.id ? { ...img, folderId: folder.id } : img
            );
            setOptimisticImages(optimisticImages);

            try {
               await assignImageToFolder(item.id, folder.id);
            } catch (error) {
               console.error("Error moving image:", error);
               // Revert the optimistic update on error
               const revertedImages = allImages.map((img) =>
                  img.id === item.id ? { ...img, folderId: img.folderId } : img
               );
               setOptimisticImages(revertedImages);
            }
            return { folderId: folder.id };
         },
         collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
         }),
      }),
      [folder, allImages]
   );

   const handleNavigate = (e: React.MouseEvent) => {
      // Stop propagation if we're clicking a button
      if ((e.target as HTMLElement).closest("button")) {
         return;
      }
      if (onNavigate) {
         onNavigate(folder.id);
      }
   };

   const createdAt = folder.createdAt ? new Date(folder.createdAt) : null;

   if (view === "grid") {
      return (
         <div
            ref={(node) => drop(node) as unknown as ConnectDropTarget}
            className={`${
               isOver && canDrop ? "ring-2 ring-blue-400 rounded-lg" : ""
            }`}
         >
            <div
               className={`border rounded-md overflow-hidden cursor-pointer hover:border-blue-300 transition-all ${
                  isOver ? "bg-blue-50" : ""
               }`}
               onClick={handleNavigate}
            >
               <div className="aspect-square bg-gray-50 relative flex items-center justify-center">
                  <FolderIcon className="h-20 w-20 text-gray-300" />

                  {/* Admin actions if provided */}
                  {(onDelete || onEdit) && (
                     <div className="absolute top-2 right-2 flex space-x-1">
                        {onEdit && (
                           <Button
                              variant="secondary"
                              size="icon"
                              className="h-7 w-7 bg-white opacity-70 hover:opacity-100"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 onEdit(folder);
                              }}
                           >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                           </Button>
                        )}
                        {onDelete && (
                           <Button
                              variant="destructive"
                              size="icon"
                              className="h-7 w-7 opacity-70 hover:opacity-100"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 onDelete(folder.id);
                              }}
                           >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                           </Button>
                        )}
                     </div>
                  )}
               </div>

               <div className="p-2 bg-white">
                  <div className="truncate text-sm font-medium">
                     {folder.name || "Untitled Folder"}
                  </div>
                  {createdAt && (
                     <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDistanceToNow(createdAt, { addSuffix: true })}
                     </div>
                  )}
               </div>
            </div>
         </div>
      );
   }

   // List view
   return (
      <div
         className="flex items-center border rounded-md p-2 cursor-pointer hover:bg-gray-50"
         onClick={handleNavigate}
      >
         <div className="h-10 w-10 bg-gray-50 rounded mr-3 flex-shrink-0 flex items-center justify-center">
            <FolderIcon className="h-6 w-6 text-gray-300" />
         </div>

         <div className="flex-1 min-w-0">
            <div className="truncate font-medium">
               {folder.name || "Untitled Folder"}
            </div>
            {createdAt && (
               <div className="flex items-center text-xs text-gray-500">
                  <Calendar className="h-3 w-3 mr-1" />
                  {formatDistanceToNow(createdAt, { addSuffix: true })}
               </div>
            )}
         </div>

         {/* Admin actions if provided */}
         {(onDelete || onEdit) && (
            <div className="flex space-x-1 ml-2">
               {onEdit && (
                  <Button
                     variant="ghost"
                     size="icon"
                     className="h-8 w-8"
                     onClick={(e) => {
                        e.stopPropagation();
                        onEdit(folder);
                     }}
                  >
                     <MoreHorizontal className="h-4 w-4" />
                  </Button>
               )}
               {onDelete && (
                  <Button
                     variant="ghost"
                     size="icon"
                     className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                     onClick={(e) => {
                        e.stopPropagation();
                        onDelete(folder.id);
                     }}
                  >
                     <MoreHorizontal className="h-4 w-4" />
                  </Button>
               )}
            </div>
         )}
      </div>
   );
};

export default FolderCard;
